// Shared Smart Time Select logic — used by BOTH the booking flow
// (BookRideSteps/modalPages/smartTimeSelect.jsx) and the edit/confirm flow
// (editRideComponents/PageModals/DateTimeSection.jsx). Pure functions only; each
// caller owns its own state/persistence (storage+step-machine vs controlled props).
import {flightService} from '@/services/flightService'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {getWaitingBucket, getAirportBufferMinutes, getArriveEarlyMinutes} from '@/utils/airports'
import {tripNow, formatTripLocalTime, roundToNext5Min, getMinLeadTimeStr} from '@/utils/dateUtils'

// Resolve the airport context from the pickup/dropoff locations. `detect` is the caller's
// `isNearAirport(lat,lng)` — both flows now pass the SAME canonical detector (the
// /config/airports list, via @/utils/airports), but it stays a param so each caller supplies
// it explicitly. Keeps the resolved airport OBJECT (carries `.code` for the flight-IATA
// compare, and `.waiting_times` for everything below).
//
// `airportBuffer` is the CUSHION the recommendation is computed from: this airport's own
// waiting_times bucket for the leg, falling back to the global booking.*_extra_minutes when
// that bucket is unconfigured. `waitingItems` are the same bucket's rows, for the
// "Why this pickup time?" breakdown. Pass `flightDetails` so the bucket can be picked by
// flight scope — with no flight yet we assume international, the longer of the two.
export function airportContext(origin, dest, detect, flightDetails = null) {
    const originAirport = origin?.lat && origin?.lng ? detect(origin.lat, origin.lng) : null
    const destAirport = dest?.lat && dest?.lng ? detect(dest.lat, dest.lng) : null
    const isFromAirport = !!originAirport
    const isToAirport = !!destAirport
    const selectedAirportCode = isFromAirport
        ? (originAirport && originAirport.code)
        : (isToAirport ? (destAirport && destAirport.code) : null)

    const legAirport = isFromAirport ? originAirport : destAirport
    const isIntlFlight = flightService.getFlightType(flightDetails) !== 'Domestic'
    const waitingOpts = {fromAirport: isFromAirport, international: isIntlFlight}
    const waitingBucket = getWaitingBucket(legAirport, waitingOpts)
    const waitingItems = waitingBucket?.items ?? []
    const airportBuffer = getAirportBufferMinutes(legAirport, waitingOpts)
    // The cushion minus its road allowance — what the "arrive ~X before departure" note means.
    const arriveEarlyMinutes = getArriveEarlyMinutes(airportBuffer, waitingItems)

    return {
        originAirport, destAirport, isFromAirport, isToAirport, selectedAirportCode,
        legAirport, waitingBucket, waitingItems, airportBuffer, arriveEarlyMinutes,
    }
}

// Copy per mismatch reason (see flightMismatchReason).
export const FLIGHT_MISMATCH_MESSAGES = {
    generic: "Your flight doesn't match your selected airport location.",
    wrongDirectionArrival: "The flight you selected is an arrival flight. For a ride to the airport, please select a departure flight instead.",
    wrongDirectionDeparture: "The flight you selected is a departure flight. For a ride from the airport, please select an arrival flight instead.",
}

// Why a flight doesn't match the selected airport — null = matches. The side that
// MUST equal the selected airport is the flight ARRIVAL (fromAirport) or DEPARTURE
// (toAirport). If the selected airport instead appears on the OPPOSITE side the user
// picked the wrong-direction flight, so surface a specific hint; else it's generic.
//  - 'wrongDirectionArrival'   : toAirport ride, selected airport is the flight ARRIVAL.
//  - 'wrongDirectionDeparture' : fromAirport ride, selected airport is the flight DEPARTURE.
//  - 'generic'                 : selected airport isn't on either side of the flight.
export function flightMismatchReason(fd, {isFromAirport, selectedAirportCode}) {
    if (!fd || !selectedAirportCode) return null
    const sel = selectedAirportCode.toUpperCase()
    const dep = (fd?.departure?.airport?.iata || '').toUpperCase()
    const arr = (fd?.arrival?.airport?.iata || '').toUpperCase()
    const wanted = isFromAirport ? arr : dep   // side that must equal `sel`
    const other = isFromAirport ? dep : arr    // opposite side
    if (wanted === sel) return null            // correct — matches
    if (other && other === sel) return isFromAirport ? 'wrongDirectionDeparture' : 'wrongDirectionArrival'
    if (!wanted) return null                   // no data on the wanted side yet — can't judge
    return 'generic'
}

// Soft validation: the looked-up flight's airport must match the selected airport —
// fromAirport ⇒ flight ARRIVAL; toAirport ⇒ flight DEPARTURE. Plain IATA compare.
export function isFlightMismatch(fd, ctx) {
    return flightMismatchReason(fd, ctx) !== null
}

// The user-facing message for a flight's mismatch ('' when it matches).
export function flightMismatchMessage(fd, ctx) {
    const reason = flightMismatchReason(fd, ctx)
    return reason ? (FLIGHT_MISMATCH_MESSAGES[reason] || FLIGHT_MISMATCH_MESSAGES.generic) : ''
}

// Cancelled flight: AeroDataBox reports status "Canceled" (also "CanceledUncertain").
// Match case-insensitively on the "cancel" prefix so British/American spellings and
// the uncertain variant all trip it.
export function isFlightCancelled(fd) {
    return typeof fd?.status === 'string' && fd.status.trim().toLowerCase().startsWith('cancel')
}

// Resolve a movement's (departure/arrival) effective time. The "new"/current time can
// live in the airline's official `revisedTime` or AeroDataBox's estimated
// `predictedTime`. Official revision wins when it deviates from scheduled; otherwise
// the prediction is used (a delay the airline hasn't formally revised yet, e.g. WS47:
// scheduled 13:20, revised 13:20 unchanged, predicted 13:29). Time and date changes are
// tracked separately so a same-day delay never strikes an unchanged date.
export function resolveMovementTime(mv) {
    const scheduled = mv?.scheduledTime?.local || ''
    const revised = mv?.revisedTime?.local || ''
    const predicted = mv?.predictedTime?.local || ''
    let effective = scheduled
    if (revised && revised !== scheduled) effective = revised
    else if (predicted && predicted !== scheduled) effective = predicted
    const timeChanged = !!effective && !!scheduled && effective !== scheduled
    const dateChanged = timeChanged
        && flightService.formatFlightDate(effective) !== flightService.formatFlightDate(scheduled)
    return {scheduled, effective, timeChanged, dateChanged}
}

// Recommended pickup time. fromAirport: arrival + buffer. toAirport: departure - (travel + buffer).
// Uses the effective (revised/predicted) time so a delayed flight shifts the pickup.
export function getRecommendedTime({flightDetails, isFromAirport, airportBuffer, travelMinutes = 0}) {
    if (!flightDetails) return null
    if (isFromAirport) {
        const arrLocal = resolveMovementTime(flightDetails.arrival).effective
        if (!arrLocal) return null
        const arrival = flightService.parseLocalTime(arrLocal)
        const recommendedTime = roundToNext5Min(new Date(arrival.getTime() + airportBuffer * 60000))
        return {flightArrival: arrival, travelMinutes: 0, recommendedTime}
    }
    const depLocal = resolveMovementTime(flightDetails.departure).effective
    if (!depLocal) return null
    const departure = flightService.parseLocalTime(depLocal)
    const recommendedTime = roundToNext5Min(new Date(departure.getTime() - (travelMinutes + airportBuffer) * 60000))
    return {flightDeparture: departure, travelMinutes, recommendedTime}
}

// Validate the recommended time against the minimum booking lead time. Returns a
// message string, or false when the time is acceptable.
export function getSmartTimeError(recommendedTime) {
    if (!recommendedTime) return false
    const now = tripNow()
    const minAllowed = new Date(now.getTime() + BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES * 60 * 1000)
    if (recommendedTime.getTime() <= now.getTime()) {
        return 'The recommended pickup time has already passed for this flight.'
    }
    if (recommendedTime.getTime() < minAllowed.getTime()) {
        const earliest = roundToNext5Min(minAllowed)
        return `For quick bookings, please contact ${BOOKING_CONSTRAINTS.CONTACT_NUMBER}. Online booking is only available ${getMinLeadTimeStr()} in advance (earliest available: ${formatTripLocalTime(earliest)}).`
    }
    return false
}

// AeroDataBox date param (Y-m-d) from a date value. undefined when no date.
export function flightDateParam(date) {
    if (!date) return undefined
    const d = new Date(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const formatSmartTime = (date) => date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})
export const formatSmartDateLabel = (date) => date.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})
